const CNPJ_MAX_LENGTH = 14;
const cnpjFirstDigitWeights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const cnpjSecondDigitWeights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

export const VALID_CNPJ_INPUT_CHAR_REGEX = /^[A-Z0-9]$/i;

export const normalizeCNPJ = (value: any): string => {
    if (!value) {
        return '';
    }

    return value
        .toString()
        .toUpperCase()
        .replaceAll(/[^A-Z0-9]/g, '')
        .slice(0, CNPJ_MAX_LENGTH);
};

export const formatCNPJ = (value: any): string => {
    const normalizedValue = normalizeCNPJ(value);

    if (!normalizedValue) {
        return '';
    }

    const part1 = normalizedValue.slice(0, 2);
    const part2 = normalizedValue.slice(2, 5);
    const part3 = normalizedValue.slice(5, 8);
    const part4 = normalizedValue.slice(8, 12);
    const part5 = normalizedValue.slice(12, 14);

    let formatted = part1;

    if (part2) formatted += `.${part2}`;
    if (part3) formatted += `.${part3}`;
    if (part4) formatted += `/${part4}`;
    if (part5) formatted += `-${part5}`;

    return formatted;
};

export const getDisplayableCNPJ = (value: any): string => {
    const rawCNPJ = value?.toString() ?? '';
    const normalizedCNPJ = normalizeCNPJ(rawCNPJ);

    if (normalizedCNPJ.length !== CNPJ_MAX_LENGTH) {
        return rawCNPJ;
    }

    return formatCNPJ(normalizedCNPJ);
};

const getCNPJCharValue = (char: string): number =>
    (char.codePointAt(0) ?? 0) - 48;

const getCNPJVerifierDigit = (cnpjBase: string, weights: number[]): number => {
    const weightedSum = cnpjBase
        .split('')
        .reduce(
            (sum, currentChar, index) =>
                sum + getCNPJCharValue(currentChar) * weights[index],
            0,
        );

    const remainder = weightedSum % 11;

    return remainder < 2 ? 0 : 11 - remainder;
};

export const isValidAlphanumericCNPJ = (value: string): boolean => {
    const cnpj = normalizeCNPJ(value);

    if (!/^[A-Z0-9]{12}\d{2}$/.test(cnpj)) {
        return false;
    }

    const cnpjWithoutVerifierDigits = cnpj.slice(0, 12);
    const firstVerifierDigit = getCNPJVerifierDigit(
        cnpjWithoutVerifierDigits,
        cnpjFirstDigitWeights,
    );
    const secondVerifierDigit = getCNPJVerifierDigit(
        `${cnpjWithoutVerifierDigits}${firstVerifierDigit}`,
        cnpjSecondDigitWeights,
    );
    const currentVerifierDigits = cnpj.slice(12, 14);

    return (
        currentVerifierDigits === `${firstVerifierDigit}${secondVerifierDigit}`
    );
};
